import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { onboardingAPI, companyAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, CheckCircle2 } from 'lucide-react';

const Onboarding = () => {
    const [searchParams] = useSearchParams();
    const companyId = searchParams.get('companyId');
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 4;

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        personal_email: '',
        date_of_birth: '',
        gender: '',
        blood_group: '',
        emergency_contact: {
            name: '',
            relationship: '',
            phone: '',
        },
        address: '',
        aadhaar: '',
        pan: '',
        pf_account_number: '',
        uan: '',
        profile_picture: '',
        bank_details: {
            account_name: '',
            account_number: '',
            bank_name: '',
            ifsc_code: '',
            branch: '',
        },

        employee_code: '', // Optional, or admin can assign later
        date_of_joining: '',
    });

    useEffect(() => {
        if (companyId) {
            fetchCompany();
        } else {
            setLoading(false);
        }
    }, [companyId]);

    const fetchCompany = async () => {
        try {
            const response = await onboardingAPI.getCompanyInfo(companyId);
            if (response.data) {
                setCompany(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch company:', error);
            toast.error('Could not load company information. Some branding may be missing.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData((prev) => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value,
                },
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        try {
            const response = await onboardingAPI.uploadProfilePicture(uploadFormData);
            setFormData(prev => ({ ...prev, profile_picture: response.data.profile_picture }));
            toast.success('Profile picture uploaded!');
        } catch (error) {
            console.error('Failed to upload profile picture:', error);
            toast.error('Failed to upload profile picture');
        }
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!companyId) {
            toast.error('Invalid onboarding link. Company ID is missing.');
            return;
        }

        setSubmitting(true);
        try {
            const response = await onboardingAPI.submit({
                ...formData,
                email: formData.email || null,
                company_id: companyId,
            });
            setSubmitted(true);
            toast.success(response?.data?.message || 'Your information has been submitted successfully!');
        } catch (error) {
            console.error('Onboarding submission error:', error);
            const message =
                error?.response?.data?.detail ||
                error?.message ||
                'Failed to submit onboarding information. Please try again.';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    const nextStep = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        // Basic validation for mandatory fields in early steps can be added here
        if (currentStep === 1) {
            if (!formData.first_name || !formData.last_name || !formData.personal_email) {
                toast.error('Please fill in all required personal information (*)');
                return;
            }
            if (!emailRegex.test(formData.personal_email)) {
                toast.error('Please enter a valid personal email address');
                return;
            }
            if (formData.email && !emailRegex.test(formData.email)) {
                toast.error('Please enter a valid official email address');
                return;
            }
        }

        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
            window.scrollTo(0, 0);
        } else {
            handleSubmit();
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            window.scrollTo(0, 0);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!companyId) {
        return (
            <div className="flex items-center justify-center min-h-screen p-4">
                <Card className="w-full max-w-md">
                    <CardHeader>
                        <CardTitle className="text-destructive">Invalid Link</CardTitle>
                        <CardDescription>
                            This onboarding link is invalid or incomplete. Please contact your HR department.
                        </CardDescription>
                    </CardHeader>
                </Card>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="flex items-center justify-center min-h-screen p-4">
                <Card className="w-full max-w-md text-center py-8">
                    <CardContent className="space-y-4">
                        <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
                        <h2 className="text-2xl font-bold">Thank You!</h2>
                        <p className="text-muted-foreground">
                            Your onboarding information has been submitted. The HR team will review your details and get back to you shortly.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted/30 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <Card>
                    <CardHeader>
                        <div className="flex flex-col items-center text-center space-y-4">
                            {company?.logo && (
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}${company.logo}`}
                                    alt={company.name}
                                    className="h-20 w-auto object-contain"
                                    onError={(e) => {
                                        console.error('Logo failed to load');
                                        e.target.style.display = 'none';
                                    }}
                                />
                            )}
                            <div className="space-y-1">
                                <CardTitle className="text-3xl font-bold">
                                    {company?.name ? `${company.name} - ` : ''}Employee Onboarding
                                </CardTitle>
                                <CardDescription>
                                    Please fill in your details to complete the onboarding process.
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {/* Progress Stepper */}
                        <div className="mb-8">
                            <div className="flex items-center justify-between mb-2">
                                {[1, 2, 3, 4].map((s) => (
                                    <div key={s} className="flex flex-col items-center flex-1">
                                        <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold mb-1 transition-colors ${currentStep >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                                            {s}
                                        </div>
                                        <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-tighter">
                                            {s === 1 ? 'Personal' : s === 2 ? 'Contact' : s === 3 ? 'Identity' : 'Bank'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="w-full bg-muted h-2 rounded-full overflow-hidden">
                                <div 
                                    className="bg-primary h-full transition-all duration-300 ease-in-out" 
                                    style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
                                />
                            </div>
                        </div>

                        <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
                            {/* Step 1: Personal Information */}
                            {currentStep === 1 && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold border-b pb-2">Personal Information</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="first_name">First Name *</Label>
                                            <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} required />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="last_name">Last Name *</Label>
                                            <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} required />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="email">Official Email (optional)</Label>
                                            <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="personal_email">Personal Email *</Label>
                                            <Input id="personal_email" name="personal_email" type="email" value={formData.personal_email} onChange={handleChange} required />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="phone">Phone Number</Label>
                                            <Input id="phone" name="phone" value={formData.phone} onChange={handleChange} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="date_of_birth">Date of Birth</Label>
                                            <Input id="date_of_birth" name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="gender">Gender</Label>
                                            <Select value={formData.gender} onValueChange={(value) => setFormData(prev => ({ ...prev, gender: value }))}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select Gender" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Male">Male</SelectItem>
                                                    <SelectItem value="Female">Female</SelectItem>
                                                    <SelectItem value="Other">Other</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="blood_group">Blood Group</Label>
                                            <Select value={formData.blood_group} onValueChange={(value) => setFormData(prev => ({ ...prev, blood_group: value }))}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select Blood Group" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="A+">A+</SelectItem>
                                                    <SelectItem value="A-">A-</SelectItem>
                                                    <SelectItem value="B+">B+</SelectItem>
                                                    <SelectItem value="B-">B-</SelectItem>
                                                    <SelectItem value="AB+">AB+</SelectItem>
                                                    <SelectItem value="AB-">AB-</SelectItem>
                                                    <SelectItem value="O+">O+</SelectItem>
                                                    <SelectItem value="O-">O-</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="profile_picture">Profile Picture</Label>
                                            <div className="flex items-center gap-4">
                                                {formData.profile_picture && (
                                                    <img 
                                                        src={`${process.env.REACT_APP_BACKEND_URL}${formData.profile_picture}`} 
                                                        alt="Profile" 
                                                        className="h-12 w-12 rounded-full object-cover border"
                                                    />
                                                )}
                                                <Input id="profile_picture" type="file" accept="image/*" onChange={handleFileChange} className="text-xs" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Contact & Address */}
                            {currentStep === 2 && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold border-b pb-2">Address & Emergency Contact</h3>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="address">Current Address</Label>
                                            <Input id="address" name="address" value={formData.address} onChange={handleChange} />
                                        </div>
                                        
                                        <div className="pt-4">
                                            <h4 className="text-sm font-medium text-muted-foreground mb-4">Emergency Contact Details</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="emergency_contact.name">Contact Name</Label>
                                                    <Input id="emergency_contact.name" name="emergency_contact.name" value={formData.emergency_contact.name} onChange={handleChange} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="emergency_contact.relationship">Relationship</Label>
                                                    <Select value={formData.emergency_contact.relationship} onValueChange={(value) => setFormData(prev => ({ ...prev, emergency_contact: { ...prev.emergency_contact, relationship: value } }))}>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="Spouse">Spouse</SelectItem>
                                                            <SelectItem value="Parent">Parent</SelectItem>
                                                            <SelectItem value="Sibling">Sibling</SelectItem>
                                                            <SelectItem value="Friend">Friend</SelectItem>
                                                            <SelectItem value="Other">Other</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="emergency_contact.phone">Primary Phone</Label>
                                                    <Input id="emergency_contact.phone" name="emergency_contact.phone" value={formData.emergency_contact.phone} onChange={handleChange} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Identification */}
                            {currentStep === 3 && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold border-b pb-2">Identification Details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="aadhaar">Aadhaar Number</Label>
                                            <Input id="aadhaar" name="aadhaar" value={formData.aadhaar} onChange={handleChange} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="pan">PAN Number</Label>
                                            <Input id="pan" name="pan" value={formData.pan} onChange={handleChange} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="pf_account_number">PF Account Number</Label>
                                            <Input id="pf_account_number" name="pf_account_number" value={formData.pf_account_number} onChange={handleChange} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="uan">UAN</Label>
                                            <Input id="uan" name="uan" value={formData.uan} onChange={handleChange} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 4: Bank Details */}
                            {currentStep === 4 && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold border-b pb-2">Bank Details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2 md:col-span-2">
                                            <Label htmlFor="bank_details.account_name">Account Holder Name</Label>
                                            <Input id="bank_details.account_name" name="bank_details.account_name" value={formData.bank_details.account_name} onChange={handleChange} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="bank_details.bank_name">Bank Name</Label>
                                            <Input id="bank_details.bank_name" name="bank_details.bank_name" value={formData.bank_details.bank_name} onChange={handleChange} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="bank_details.account_number">Account Number</Label>
                                            <Input id="bank_details.account_number" name="bank_details.account_number" value={formData.bank_details.account_number} onChange={handleChange} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="bank_details.ifsc_code">IFSC Code</Label>
                                            <Input id="bank_details.ifsc_code" name="bank_details.ifsc_code" value={formData.bank_details.ifsc_code} onChange={handleChange} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="bank_details.branch">Branch</Label>
                                            <Input id="bank_details.branch" name="bank_details.branch" value={formData.bank_details.branch} onChange={handleChange} />
                                        </div>
                                    </div>

                                </div>
                            )}

                            {/* Navigation Buttons */}
                            <div className="flex justify-between pt-6 border-t">
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={prevStep} 
                                    disabled={currentStep === 1 || submitting}
                                >
                                    Back
                                </Button>
                                
                                <Button 
                                    type="button" 
                                    onClick={nextStep} 
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Submitting...
                                        </>
                                    ) : currentStep === totalSteps ? (
                                        'Submit All Details'
                                    ) : (
                                        'Next Step'
                                    )}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default Onboarding;
